import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, TargetIcon, SparklesIcon, ShieldCheckIcon } from 'lucide-react';
// Search Everywhere Optimization staat op #1 — biggest selling point
const pillars = [{
  icon: SparklesIcon,
  label: 'Search Everywhere Optimization',
  desc: <>
        Je wordt niet alleen zichtbaar in Google, maar{' '}
        <strong className="font-bold text-brand-dark" data-id="element-399">overal</strong> waar je
        klanten zoeken, ook in{' '}
        <strong className="font-bold text-brand-dark" data-id="element-400">
          alle AI-gedreven zoekmachines
        </strong>
        .
      </>
}, {
  icon: DatabaseIcon,
  label: 'Sonar vóór structuur',
  desc: <>
        We gissen niet waar we moeten vissen. We{' '}
        <strong className="font-bold text-brand-dark" data-id="element-401">meten</strong> het eerst
        met{' '}
        <strong className="font-bold text-brand-dark" data-id="element-402">echte Ads-data</strong>.
      </>
}, {
  icon: TargetIcon,
  label: 'Marktexclusiviteit',
  desc: <>
        Met platinum werken we{' '}
        <strong className="font-bold text-brand-dark" data-id="element-403">
          per markt met één klant
        </strong>
        . Zodra jij start, sluit die markt voor je concurrenten.{' '}
        <strong className="font-bold text-brand-dark" data-id="element-404">
          Zo kunnen we alles op alles zetten dat jij boven je concurrent komt.
        </strong>
      </>
}, {
  icon: ShieldCheckIcon,
  label: 'Jouw data, jouw goud',
  desc: <>
        We gebruiken jouw CorePrint data{' '}
        <strong className="font-bold text-brand-dark" data-id="element-405">alleen voor jou</strong>.{' '}
        <strong className="font-bold text-brand-dark" data-id="element-406">
          Jij bent eigenaar van de data
        </strong>
        . Dit is jouw goud.
      </>
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
function SonarPulse() {
  return <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none z-0 opacity-40" data-id="element-407">
      {[0, 1, 2].map(i => <motion.div key={i} className="absolute inset-0 rounded-full border-[2px] border-brand-primary/20" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: [0, 1],
      opacity: [0.8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      delay: i * 1.33,
      ease: 'easeOut'
    }} data-id="element-408" />)}
    </div>;
}
export function WhatIs() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 relative overflow-hidden" style={{
    background: 'linear-gradient(180deg, #ffffff 0%, #F8FAFC 100%)'
  }} data-id="element-409">
      <div className="max-w-[1200px] mx-auto px-6 relative z-10" data-id="element-410">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-411">
          {/* Left */}
          <motion.div className="relative" initial={{
          opacity: 0,
          x: -40
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          type: 'spring',
          stiffness: 80
        }} data-id="element-412">
            <SonarPulse data-id="element-413" />
            <div className="relative z-10" data-id="element-414">
              <div className="section-label" data-id="element-415">De vierde visser</div>
              <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-brand-dark leading-tight tracking-tight mb-7" data-id="element-416">
                De visser met sonar
                <br data-id="element-417" />
                <span className="text-brand-primary" data-id="element-418">
                  eerst peilen, dan vissen
                </span>
              </h2>
              <p className="text-brand-gray leading-relaxed text-[1.08rem] mb-6" data-id="element-419">
                Iedereen bouwt een net (meer pagina's, meer structuur), maar
                niemand kijkt eerst waar de vis daadwerkelijk zit. CorePrint is
                de{' '}
                <strong className="text-brand-dark font-bold" data-id="element-420">
                  sonar-aanpak
                </strong>{' '}
                voor B2B-bedrijven.
              </p>
              <p className="text-brand-gray leading-relaxed text-[1.08rem] mb-10" data-id="element-421">
                We gebruiken 90 dagen aan echte marktdata om te bewijzen welke
                zoekwoorden daadwerkelijk leiden tot aanvragen. Dan, en pas dan,
                gooien we het net uit op de exacte juiste plek.
              </p>
              <motion.a href="#gesprek" className="btn-primary" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-422">
                Plan een gratis presentatie
              </motion.a>
            </div>
          </motion.div>

          {/* Right: pillars — Search Everywhere Optimization staat bovenaan */}
          <motion.div className="grid grid-cols-2 gap-5 relative z-10" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-423">
            {pillars.map(p => <motion.div key={p.label} className="rounded-3xl p-7 border border-[#E8EDF8] bg-white/80 backdrop-blur-md transition-all duration-500 group cursor-default" variants={itemVariants} whileHover={{
            y: -6,
            boxShadow: '0 12px 48px rgba(0, 82, 204, 0.12)',
            transition: {
              type: 'spring',
              stiffness: 300,
              damping: 20
            }
          }} data-id="element-424">
                <motion.div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{
              background: '#EFF6FF',
              border: '1px solid rgba(0, 82, 204, 0.15)'
            }} whileHover={{
              scale: 1.1,
              rotate: 5,
              transition: {
                type: 'spring',
                stiffness: 300
              }
            }} data-id="element-425">
                  <p.icon size={22} className="text-brand-primary" data-id="element-426" />
                </motion.div>
                <div className="font-black text-brand-dark text-[1rem] mb-3 group-hover:text-brand-primary transition-colors duration-300" data-id="element-427">
                  {p.label}
                </div>
                <div className="text-brand-gray text-[0.88rem] leading-relaxed" data-id="element-428">
                  {p.desc}
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}